import React, { useEffect, useState, useRef } from 'react';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Plus, Minus, X, GripVertical } from 'lucide-react';

interface AdjustSequenceWidgetProps {
  visible: boolean;
  selectedCount: number;
  position?: { x: number; y: number };
  onIncrement: () => void;
  onDecrement: () => void;
  onClose: () => void;
}

export const AdjustSequenceWidget: React.FC<AdjustSequenceWidgetProps> = ({
  visible,
  selectedCount,
  position,
  onIncrement,
  onDecrement,
  onClose,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState(position || { x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  // Update position when prop changes
  useEffect(() => {
    if (position) {
      setCurrentPosition(position);
    }
  }, [position]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setCurrentPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleDragStart = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  if (!visible || selectedCount === 0) return null;

  // Use currentPosition or default to center-bottom
  const style = currentPosition.x !== 0 || currentPosition.y !== 0
    ? {
        left: `${currentPosition.x}px`,
        top: `${currentPosition.y}px`,
      }
    : {
        left: '50%',
        bottom: '100px',
        transform: 'translateX(-50%)',
      };

  return (
    <Card 
      ref={cardRef}
      className="fixed z-[90] p-4 shadow-xl border-2 border-primary" 
      style={{ ...style, cursor: isDragging ? 'grabbing' : 'auto' }}
    >
      <div className="flex items-center gap-4">
        <div 
          className="cursor-grab active:cursor-grabbing"
          onMouseDown={handleDragStart}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <span className="text-sm font-semibold">
          Adjust Sequence ({selectedCount} marker{selectedCount !== 1 ? 's' : ''})
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onDecrement}
            className="h-10 w-10 p-0"
          >
            <Minus className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onIncrement}
            className="h-10 w-10 p-0"
          >
            <Plus className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

